import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBuilding,
  faFileAlt,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';
import { OfflineCompletedScreenStyles } from '../CompletedQuotes/completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from '../CompletedQuotes/CompletedV2.styles';
import CompletedQuoteScreenOfflineInfo from '../../common/components/elements/CompletedQuoteScreenOfflineInfo';
import CompletedScreenOfflineDocuments from '../../common/components/elements/CompletedScreenOfflineDocuments';
import ErrorComponent from '../../common/components/ErrorComponent';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { apiDownloadDocument, documentMethodManualUpload } from '../../services/axios-client/axeCommons';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import { getOfflineQuoteCompletedData, getOfflineQuoteCompletedDataReset } from '../../features/purchases/purchasesActions';
import {
  generalAlertError,
} from '../../common/utils/alerts';
import { sonnerSuccessAlert } from '../../common/utils/sonnerAlerts';
import CompletedRiskQuoteScreenOfflineInfo from '../../common/components/elements/CompletedRiskQuoteScreenOfflineInfo';
import ContractDocumentsOffline from '../../common/components/elements/ContractDocumentsOffline';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';
import CompletedScreenSemestraleButton from '../../common/components/elements/CompletedScreenSemestraleButton';
import CompletedScreenCustomerLinkButton from '../../common/components/elements/CompletedScreenCustomerLinkButton';

function OfflineCompletedScreen() {
  const { quoteId } = useParams();
  const {
    loading, quote, error, insurance_request, risk_quotations,
  } = useSelector((store) => store.purchases.offline_insurances_completed);
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );
  const [residenceInfo, setResidenceInfo] = useState(null);
  const navigate = useNavigate();
  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);

  const collectQuoteData = () => {
    getOfflineQuoteCompletedData(quoteId);
  };

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find((commune) => commune.cadastral_code == insurance_request.customer.residence_commune_code);
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  const uploadSignedDocument = async (documentId, files) => {
    const uploadedFile = files[0];
    const reader = new FileReader();

    reader.readAsDataURL(uploadedFile);
    if (uploadedFile.type !== 'application/pdf') {
      generalAlertError('Formato di file errato. Il file deve essere in .pdf');
      return;
    }
    reader.onload = () => {
      documentMethodManualUpload(quote.id, documentId, {
        document: reader.result.replace('data:application/pdf;base64,', ''),
      }).then(() => {
        collectQuoteData();
        sonnerSuccessAlert('File caricato');
      });
    };
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      getOfflineQuoteCompletedDataReset();
    };
  }, [quoteId]);

  if (loading && quote === null) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }

  return (
    <OfflineCompletedScreenStyles>
      {quote.payments.length === 1 && quote.status === 4 && quote.payment_frequency === 2 && (
        <CompletedScreenSemestraleButton
          onNavigate={() => navigate(`/quotes/commercial/purchase/${quote.insurance_request.request_token}`)}
          buttonText="Procedi al pagamento"
          disabled={false}
          v2
        />
      )}
      {quote.status === 7 && quote.insurance_request.active_customer_id && (
        <CompletedScreenCustomerLinkButton
          onNavigate={() => {
            navigate(`/clients/${quote.insurance_request.active_customer_id}`);
          }}
          buttonText="Apri dettaglio cliente"
          disabled={false}
          v2
        />
      )}
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocumentsOffline
          v2
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
        />
      </div>

      {/* Customer Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon
                    icon={quote.insurance_request.customer.gender === 'G' ? faBuilding : faUser}
                  />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>
                    {quote.insurance_request.customer.gender === 'G' ? 'Compagnia' : 'Nome e Cognome'}
                  </dt>
                  <dd>
                    {quote.insurance_request.customer.gender === 'G'
                      ? quote.insurance_request.customer.business_name
                      : `${quote.insurance_request.customer.name} ${quote.insurance_request.customer.surname}`}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{quote.insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{quote.insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {quote.insurance_request.customer.address}
                    ,
                    {' '}
                    {quote.insurance_request.customer.house_number}
                    ,
                    {' '}
                    {quote.insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Prodotto</dt>
                  <dd>{quote.product.name}</dd>
                </CompletedField>

                {quote.cauzioni && (
                  <>
                    <CompletedField>
                      <dt>Garanzia</dt>
                      <dd>{`${quote.cauzioni.sub_product_name} — ${quote.cauzioni.risk_name}`}</dd>
                    </CompletedField>
                    <CompletedField>
                      <dt>Decorrenza</dt>
                      <dd>
                        {quote.cauzioni.effective_date ? new Date(quote.cauzioni.effective_date).toLocaleDateString('it-IT') : '-'}
                      </dd>
                    </CompletedField>
                    <CompletedField>
                      <dt>Scadenza</dt>
                      <dd>
                        {quote.cauzioni.expiry_date ? new Date(quote.cauzioni.expiry_date).toLocaleDateString('it-IT') : '-'}
                      </dd>
                    </CompletedField>
                    <CompletedField>
                      <dt>Somma garantita</dt>
                      <dd>
                        {quote.cauzioni.guaranteed_amount != null
                          ? `€ ${Number(quote.cauzioni.guaranteed_amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </dd>
                    </CompletedField>
                    <CompletedField className="full">
                      <dt>Beneficiario</dt>
                      <dd>{quote.cauzioni.beneficiary_business_name || '-'}</dd>
                    </CompletedField>
                    {quote.cauzioni.description && (
                      <CompletedField className="full">
                        <dt>Descrizione</dt>
                        <dd>{quote.cauzioni.description}</dd>
                      </CompletedField>
                    )}
                  </>
                )}
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>

      {/* Quote Details & Documents Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Preventivo e Documenti</h2>
        </CompletedSectionTitle>

        <div className="row">
          <CompletedQuoteScreenOfflineInfo
            rc_quotation={quote}
            quoteTypes={quoteTypes}
          />

          {risk_quotations.map((riskQuote) => (
            <CompletedRiskQuoteScreenOfflineInfo
              key={riskQuote.id}
              rc_quotation={riskQuote}
              risk={riskQuote}
            />
          ))}

          <CompletedScreenOfflineDocuments
            rc_quotation={quote}
            insuranceRequest={insurance_request}
            downloadDocument={downloadDocument}
            uploadSignedDocument={uploadSignedDocument}
          />
        </div>
      </div>

      {/* After Sale Section */}
      <CompletedQuoteScreenAutoAfterSale
        quote={quote}
        document_types={document_types}
        v2
      />
    </OfflineCompletedScreenStyles>
  );
}

export default OfflineCompletedScreen;
